import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useNavigation } from "../../utilities/NavigationContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

/**
 * Backend base URL (Create React App style env var)
 * e.g. https://api.caredevo.com
 */
const EndPoint = process.env.REACT_APP_API_ENDPOINT;

/**
 * ✅ Class component for your register form
 * - Uses a honeypot field ("company") + reCAPTCHA v3 token on submit
 * - Hooks are NOT used here (hooks can't run in class components)
 * - The reCAPTCHA "executeRecaptcha" function is injected via props by a wrapper below
 */
class Register extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // Core fields
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      passwordConfirm: "",

      // Select field (your UI calls it userType; label says "Role")
      userType: "",

      // You hard-coded role: "user" in payload; keep it here for clarity
      role: "user",

      // Survey questions
      q1: "",
      q1Detail: "",
      q2Detail: "",
      q3: "",
      q3Detail: "",

      // UI helpers
      passwordMatch: true,
      isSubmitting: false,

      // ✅ Honeypot field (bots tend to fill; humans never see)
      company: "",
    };
  }

  /**
   * Handle any input changes (text/select/textarea)
   * Also keeps passwordMatch in sync as user types.
   */
  handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for password fields to keep "match" state updated
    if (name === "password" || name === "passwordConfirm") {
      const match =
        name === "password"
          ? value === this.state.passwordConfirm
          : value === this.state.password;

      this.setState({ [name]: value, passwordMatch: match });
      return;
    }

    this.setState({ [name]: value });
  };

  /**
   * Builds a single "comment" text block from your onboarding questions.
   * This keeps backend payload clean while preserving detail.
   */
  buildCombinedComment = () => {
    const { q1, q1Detail, q2Detail, q3, q3Detail } = this.state;

    return [
      `1) Are you presently overseeing patients with multiple chronic conditions and complex consultations? Answer: ${
        q1 || "not answered"
      }`,
      q1Detail?.trim() ? `   Details: ${q1Detail.trim()}` : null,

      `2) What kind of care plan strategies do you use?`,
      q2Detail?.trim() ? `   ${q2Detail.trim()}` : null,

      `3) Do you think a live / always-up-to-date care plan is important? Answer: ${
        q3 || "not answered"
      }`,
      q3Detail?.trim() ? `   Details: ${q3Detail.trim()}` : null,
    ]
      .filter(Boolean)
      .join("\n");
  };

  /**
   * Submit handler
   * Order matters:
   *  1) Honeypot check (cheap, blocks dumb bots)
   *  2) Validate fields
   *  3) Get reCAPTCHA token (v3)
   *  4) POST to backend
   */
  handleSubmit = async (e) => {
    e.preventDefault();

    const {
      firstName,
      lastName,
      email,
      password,
      passwordConfirm,
      userType,
      role,
      q1,
      q3,
      company,
    } = this.state;

    // ✅ 1) Honeypot (bots often auto-fill everything)
    // If company is filled, silently do nothing (or show a generic toast if you prefer).
    if (company && company.trim().length > 0) {
      return;
    }

    // ✅ 2) Client-side validation
    if (!firstName.trim()) return toast.error("Please enter your first name.");
    if (!lastName.trim()) return toast.error("Please enter your last name.");
    if (!email.trim()) return toast.error("Please enter your email.");
    if (!userType.trim()) return toast.error("Please select your role.");
    if (!password) return toast.error("Please enter a password.");
    if (password !== passwordConfirm) return toast.error("Passwords do not match!");
    if (!q1 || !q3) return toast.error("Please answer Yes/No for questions 1 and 3.");

    const comment = this.buildCombinedComment();
    if (!comment.trim()) return toast.error("Please add some comments.");

    // ✅ 3) reCAPTCHA token (injected via props)
    const { executeRecaptcha } = this.props;

    // executeRecaptcha is sometimes undefined on first load
    if (!executeRecaptcha) {
      return toast.error("reCAPTCHA is loading—please try again.");
    }

    this.setState({ isSubmitting: true });

    let tokenCap;
    try {
      // "subscribe" should match what you check on backend: response.data.action === "subscribe"
      tokenCap = await executeRecaptcha("subscribe");
    } catch (err) {
      this.setState({ isSubmitting: false });
      return toast.error("reCAPTCHA failed—please try again.");
    }

    // ✅ 4) Submit to backend
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        passwordConfirm,
        userType: userType.trim(),
        role, // "user"
        comment,

        // IMPORTANT: keep naming consistent with backend
        tokenCap,

        // Honeypot: should be empty
        company,
      };

      const res = await axios.post(`${EndPoint}/user`, payload);

      toast.success(res.data?.message || "Registered successfully!");
      this.props.navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error registering");
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const {
      firstName,
      lastName,
      email,
      password,
      passwordConfirm,
      userType,
      q1,
      q1Detail,
      q2Detail,
      q3,
      q3Detail,
      isSubmitting,
      company,
    } = this.state;

    return (
      <div>
        {/* Decorative background blocks (your original) */}
        <div className="absolute -mt-56 -ml-[900px] bg-gradient-to-b from-sky-700 via-sky-800 to-sky-900 w-[1500px] h-[1500px] rounded-xl rotate-20 opacity-30 z-40 xs:hidden sm:hidden" />
        <div className="absolute -mt-64 -ml-[800px] bg-gradient-to-b from-sky-700 via-sky-800 to-sky-900 w-[1500px] h-[1500px] rounded-xl rotate-20 opacity-30 z-40 xs:hidden sm:hidden" />

        <div className="flex flex-col items-center h-full bg-gradient-to-b from-sky-900 to-sky-800 py-16">
          <form onSubmit={this.handleSubmit} className="p-10 z-50">
            <h2 className="text-3xl my-4 text-sky-300 font-semibold text-center">
              Registration
            </h2>
            <h3 className="text-2xl text-sky-200 text-center">
              Streaming Chronic Care Management for
            </h3>
            <h3 className="text-2xl mb-10 text-sky-200 text-center">
              Better and Safer Outcomes
            </h3>

            <div className="w-[600px] p-0.5 rounded-xl bg-gradient-to-r from-sky-600 to-sky-800 xs:w-[500px] sm:w-[500px]">
              <div className="flex flex-col p-10 gap-6 w-auto rounded-xl bg-sky-900">
                {/* First name */}
                <input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={firstName}
                  onChange={this.handleInputChange}
                  className="rounded border border-sky-700 bg-sky-800 p-4 text-sky-100 text-sm placeholder:text-sky-400 focus:border-sky-600"
                />

                {/* Last name */}
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={this.handleInputChange}
                  className="rounded border border-sky-700 bg-sky-800 p-4 text-sky-100 text-sm placeholder:text-sky-400 focus:border-sky-600"
                />

                {/* Email */}
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={email}
                  onChange={this.handleInputChange}
                  className="rounded border border-sky-700 bg-sky-800 p-4 text-sky-100 text-sm placeholder:text-sky-400 focus:border-sky-600"
                />

                {/* Password */}
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={password}
                  onChange={this.handleInputChange}
                  className="rounded border border-sky-700 bg-sky-800 p-4 text-sky-100 text-sm placeholder:text-sky-400 focus:border-sky-600"
                />

                {/* Confirm password */}
                <input
                  type="password"
                  name="passwordConfirm"
                  placeholder="Confirm Password"
                  value={passwordConfirm}
                  onChange={this.handleInputChange}
                  className="rounded border border-sky-700 bg-sky-800 p-4 text-sky-100 text-sm placeholder:text-sky-400 focus:border-sky-600"
                />

                {/* User type */}
                <select
                  name="userType"
                  value={userType}
                  onChange={this.handleInputChange}
                  required
                  className="rounded border border-sky-700 bg-sky-800 p-4 text-sky-200 text-sm focus:border-sky-600"
                >
                  <option value="" disabled>
                    Select your Role
                  </option>
                  <option value="GP">GP</option>
                  <option value="Specialist">Specialist</option>
                  <option value="Allied Health">Allied Health</option>
                  <option value="Others">Others</option>
                </select>

                {/* Q1 */}
                <div className="bg-sky-950/30 p-4 rounded-lg border border-sky-800">
                  <p className="text-sky-200 text-sm mb-3">
                    1) Are you presently overseeing patients with multiple chronic
                    conditions and complex consultations?
                  </p>

                  <div className="flex gap-6 mb-3 text-sky-200 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="q1"
                        value="yes"
                        checked={q1 === "yes"}
                        onChange={this.handleInputChange}
                      />
                      Yes
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="q1"
                        value="no"
                        checked={q1 === "no"}
                        onChange={this.handleInputChange}
                      />
                      No
                    </label>
                  </div>

                  <textarea
                    name="q1Detail"
                    value={q1Detail}
                    onChange={this.handleInputChange}
                    placeholder="Brief details or examples..."
                    className="w-full rounded border border-sky-700 bg-sky-800 p-3 text-sky-100 text-sm h-20 resize-none"
                  />
                </div>

                {/* Q2 */}
                <div className="bg-sky-950/30 p-4 rounded-lg border border-sky-800">
                  <p className="text-sky-200 text-sm mb-3">
                    2) What kind of care plan strategies do you use (e.g., update
                    every consultation, separate notes, none)?
                  </p>

                  <textarea
                    name="q2Detail"
                    value={q2Detail}
                    onChange={this.handleInputChange}
                    placeholder="Describe your current strategy..."
                    className="w-full rounded border border-sky-700 bg-sky-800 p-3 text-sky-100 text-sm h-20 resize-none"
                  />
                </div>

                {/* Q3 */}
                <div className="bg-sky-950/30 p-4 rounded-lg border border-sky-800">
                  <p className="text-sky-200 text-sm mb-3">
                    3) Do you think a live / always-up-to-date care plan is important?
                    If yes, why?
                  </p>

                  <div className="flex gap-6 mb-3 text-sky-200 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="q3"
                        value="yes"
                        checked={q3 === "yes"}
                        onChange={this.handleInputChange}
                      />
                      Yes
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="q3"
                        value="no"
                        checked={q3 === "no"}
                        onChange={this.handleInputChange}
                      />
                      No
                    </label>
                  </div>

                  <textarea
                    name="q3Detail"
                    value={q3Detail}
                    onChange={this.handleInputChange}
                    placeholder="Why or why not?"
                    className="w-full rounded border border-sky-700 bg-sky-800 p-3 text-sky-100 text-sm h-20 resize-none"
                  />
                </div>

                {/* ✅ Honeypot field: off-screen (NOT display:none) */}
                {/* Bots that auto-fill "all inputs" often fill this; humans won't see it */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <label>
                    Company
                    <input
                      type="text"
                      name="company"
                      value={company}
                      onChange={this.handleInputChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>
                </div>

                {/* Submit */}
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`rounded-lg shadow-lg w-40 text-white px-4 py-2 ${
                      isSubmitting
                        ? "bg-sky-600/60 cursor-not-allowed"
                        : "bg-sky-600 hover:bg-sky-700"
                    }`}
                  >
                    {isSubmitting ? "Creating..." : "Create Account"}
                  </button>
                </div>

                <Link to="/" className="text-sky-300 text-center">
                  Already have an account? Login here.
                </Link>
              </div>
            </div>
          </form>
        </div>

        <ToastContainer />
      </div>
    );
  }
}

/**
 * ✅ Wrapper function component
 * - Hooks live here (allowed)
 * - Inject executeRecaptcha into the class component as a prop
 * - Also injects your NavigationContext navigate function
 */
const RegisterWithNavigation = (props) => {
  const navigate = useNavigation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  return (
    <Register
      {...props}
      navigate={navigate}
      executeRecaptcha={executeRecaptcha}
    />
  );
};

export default RegisterWithNavigation;